import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, Loader2, Mail, MailCheck, MailWarning } from 'lucide-react'
import { JarvisOrb } from '@/components/JarvisOrb'
import { apiClient, unwrap } from '@/lib/apiClient'

// Public route. Stripe Checkout redirects buyers here with ?session_id=cs_...
// after a successful one-time purchase. We hit our own /api/checkout/session
// endpoint to confirm the payment cleared (so randoms can't just type the URL
// and see a fake "thank you"), then show a confirmation + resend button +
// gentle CTA to log into the platform.

interface SessionInfo {
  paid: boolean
  productName?: string
  productSlug?: string
  email?: string | null  // masked
  firstName?: string | null
  amountCents?: number | null
  status?: string
}

export function ThanksPage() {
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')
  const slug = params.get('slug')

  const [info, setInfo] = useState<SessionInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [resending, setResending] = useState(false)
  const [resendResult, setResendResult] = useState<'idle' | 'sent' | 'failed'>('idle')
  const [resendError, setResendError] = useState<string | null>(null)

  useEffect(() => {
    if (!sessionId) {
      setLoadError('Missing session_id — this page only shows after a Stripe checkout.')
      setLoading(false)
      return
    }
    let cancelled = false
    apiClient
      .get(`/checkout/session/${encodeURIComponent(sessionId)}`)
      .then((r) => unwrap<SessionInfo>(Promise.resolve(r)))
      .then((d) => !cancelled && setInfo(d))
      .catch((e) => !cancelled && setLoadError((e as Error).message))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [sessionId])

  const handleResend = async () => {
    if (!sessionId) return
    setResending(true)
    setResendError(null)
    try {
      await unwrap<{ sent: boolean }>(
        apiClient.post(`/checkout/session/${encodeURIComponent(sessionId)}/resend`, {}),
      )
      setResendResult('sent')
    } catch (e) {
      setResendResult('failed')
      setResendError((e as Error).message)
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-jarvis-dark text-jarvis-text">
      <header className="border-b border-jarvis-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-jarvis-blue font-semibold tracking-widest text-sm">
            JARVIS
          </a>
          <a
            href="/login"
            className="text-xs text-jarvis-muted hover:text-jarvis-text transition-colors"
          >
            Sign in →
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-jarvis-blue" />
          </div>
        )}

        {!loading && (loadError || !info) && (
          <div className="text-center py-12">
            <h1 className="text-2xl font-semibold">Something looks off.</h1>
            <p className="text-jarvis-muted mt-3">
              {loadError ?? "We couldn't find that checkout session."}
            </p>
            <p className="text-jarvis-muted text-sm mt-6">
              If you just paid and got an email, you're fine — that's your real
              confirmation. If you didn't, email{' '}
              <a
                href="mailto:support@yourproject.ai"
                className="text-jarvis-blue hover:underline"
              >
                support@yourproject.ai
              </a>{' '}
              and we'll sort it out.
            </p>
          </div>
        )}

        {!loading && info && !info.paid && (
          <div className="text-center py-12">
            <h1 className="text-2xl font-semibold">Payment still processing.</h1>
            <p className="text-jarvis-muted mt-3">
              Status: <code className="text-jarvis-blue">{info.status ?? 'pending'}</code>. This is
              usually instant. Give it 30 seconds and refresh, or email{' '}
              <a
                href="mailto:support@yourproject.ai"
                className="text-jarvis-blue hover:underline"
              >
                support@yourproject.ai
              </a>{' '}
              if it doesn't clear.
            </p>
          </div>
        )}

        {!loading && info && info.paid && (
          <>
            <div className="flex flex-col items-center text-center mb-8">
              <JarvisOrb state="speaking" size="md" />
              <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-jarvis-green">
                <Check size={14} /> Payment received
              </div>
              <h1 className="text-4xl font-bold tracking-tight mt-3">
                {info.firstName ? `Thank you, ${info.firstName}.` : 'Thank you.'}
              </h1>
              <p className="text-jarvis-muted mt-4 max-w-lg leading-relaxed">
                {info.productName} is on its way to your inbox right now.
                {info.amountCents != null && (
                  <>
                    {' '}
                    Charged{' '}
                    <span className="text-jarvis-text">
                      ${(info.amountCents / 100).toFixed(2)}
                    </span>{' '}
                    to your card.
                  </>
                )}
              </p>
            </div>

            <div className="border border-jarvis-border rounded-xl bg-jarvis-surface/60 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-jarvis-blue flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm">Check your inbox</div>
                  <div className="text-xs text-jarvis-muted mt-1">
                    A confirmation email with your download link should land at{' '}
                    <span className="text-jarvis-text">{info.email ?? 'your address'}</span>{' '}
                    within a minute. The link works for 30 days.
                  </div>
                  <div className="text-xs text-jarvis-muted mt-1">
                    Not in your inbox? Check spam — first emails from a new
                    domain sometimes get filtered. Marking it "Not spam" once
                    trains the filter.
                  </div>
                </div>
              </div>

              <div className="border-t border-jarvis-border/50 pt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-jarvis-muted">Still didn't get it?</div>
                {resendResult === 'sent' ? (
                  <span className="text-xs text-jarvis-green flex items-center gap-1.5">
                    <MailCheck size={14} /> Resent — check again in a few seconds
                  </span>
                ) : resendResult === 'failed' ? (
                  <span className="text-xs text-jarvis-red flex items-center gap-1.5">
                    <MailWarning size={14} />
                    {resendError ?? 'Resend failed — email support'}
                  </span>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="text-xs px-4 py-2 bg-jarvis-blue hover:bg-jarvis-blue-bright text-white rounded-lg transition-colors disabled:opacity-60 flex items-center gap-1.5"
                  >
                    {resending && <Loader2 size={12} className="animate-spin" />}
                    Resend email
                  </button>
                )}
              </div>
            </div>

            <div className="mt-10 border border-jarvis-blue/30 rounded-xl bg-gradient-to-br from-jarvis-blue/10 to-transparent p-6">
              <div className="text-xs uppercase tracking-widest text-jarvis-blue">
                While you're here
              </div>
              <h2 className="text-xl font-semibold mt-2">
                Try Jarvis itself.
              </h2>
              <p className="text-sm text-jarvis-muted mt-2 leading-relaxed">
                The playbook you just bought is the *how*. Jarvis is the *what* — the AI chief of staff
                that runs the business while you ship the next thing. Activates
                immediately, cancel any time.
              </p>
              <div className="mt-4 flex gap-3 flex-wrap">
                <a
                  href="/login"
                  className="text-sm px-4 py-2 bg-jarvis-blue hover:bg-jarvis-blue-bright text-white rounded-lg transition-colors"
                >
                  Get Started →
                </a>
                <a
                  href="/playbook"
                  className="text-sm px-4 py-2 border border-jarvis-border hover:border-jarvis-blue/40 text-jarvis-text rounded-lg transition-colors"
                >
                  Back to the playbook
                </a>
              </div>
            </div>

            <div className="text-xs text-jarvis-muted text-center mt-10">
              Questions?{' '}
              <a
                href="mailto:support@yourproject.ai"
                className="text-jarvis-blue hover:underline"
              >
                support@yourproject.ai
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  )
}
